import { Carousel, CarouselContent, CarouselDots, CarouselItem } from '@/components/ui/carousel.tsx'
import {
	AiQueryResponse,
	LibraryVocabulary,
	PracticeSet,
	PracticeSetAnswer,
	PracticeSetItemSentence,
} from '@/models.ts'
import { useTranslation } from 'react-i18next'
import useGlobalStore from '@/useGlobalStore.ts'
import { Textarea } from '@/components/ui/textarea.tsx'
import {
	Check,
	CheckCheck,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	CircleAlert,
	Languages,
	Loader2,
	PenLine,
	Sparkles,
	X,
} from 'lucide-react'
import { useTheme } from '@/useTheme.ts'
import { Button } from '@/components/ui/button.tsx'
import { useMemo, useRef, useState } from 'react'
import { dispatch } from '@/useBus.ts'
import api from '@/api.ts'
import { toast } from 'sonner'
import { AnimatePresence, motion } from 'motion/react'
import { ExpandableButton } from '@/components/ui/expandable-button.tsx'
import useWordDetailsModalStore from '@/useWordDetailsModalStore.ts'
import remarkBreaks from 'remark-breaks'
import Markdown from 'react-markdown'
import { LoadingButton } from '@/components/loading-button.tsx'
import PracticeSetResultSummary from '@/components/practice-set-result-summary.tsx'

interface PracticeItemSentenceResult {
	is_correct: boolean
	sentence: string
	user_translation: string | null
	correction_notes: string | null
	correct_translation: string
	vocabulary_used: LibraryVocabulary[]
}

interface SentencesPracticeAnswer extends PracticeSetAnswer {
	result: Record<number, PracticeItemSentenceResult>
}

interface SentencesPracticeProps {
	practiceSet: PracticeSet
	practiceSetItems: PracticeSetItemSentence[]
	onPracticeAnswersSubmitted: () => void
	onClosePracticeSet: () => void
	answerResultCompleted: any
}

const SentencesPractice = ({
	practiceSet,
	practiceSetItems,
	onPracticeAnswersSubmitted,
	onClosePracticeSet,
	answerResultCompleted,
}: SentencesPracticeProps) => {
	const { t } = useTranslation()
	const { themeColor } = useTheme()
	const {
		availableAppLanguages,
		availableLearningLanguages,
		appLanguageCode,
		learningLanguageCode,
		currentPracticeStatus,
	} = useGlobalStore()
	const [isSubmittingAnswersLoading, setIsSubmittingAnswersLoading] = useState(false)
	const [answers, setAnswers] = useState<Record<number, string>>({})
	const [answerResult, setAnswerResult] = useState<SentencesPracticeAnswer>(answerResultCompleted || undefined)
	const { showModal, setMode, setData } = useWordDetailsModalStore()
	const [loadingExplainMoreId, setLoadingExplainMoreId] = useState<number | null>(null)
	const [followUpChatContent, setFollowUpChatContent] = useState('')
	const [isFollowUpChatContentBoxShown, setIsFollowUpChatContentBoxShown] = useState<number | null>(null)
	const [loadingSubmitFollowUpChatId, setLoadingSubmitFollowUpChatId] = useState<number | null>(null)
	const [isTextareaFocused, setIsTextareaFocused] = useState(false)
	const carouselRef = useRef<HTMLDivElement>(null)

	const handleTextAreaChange = (id: number, value: string) => {
		setAnswers((prev: any) => {
			const updatedAnswers = { ...prev }
			if (value === '') {
				delete updatedAnswers[id]
			} else {
				updatedAnswers[id] = value
			}
			return updatedAnswers
		})
	}

	const handleOpenWordDetailsModal = (libraryVocabulary: LibraryVocabulary) => {
		setData(libraryVocabulary)
		setMode('saved/no-edit')
		showModal()
	}

	const remainingAnswersCount = useMemo(() => {
		return practiceSetItems.length - Object.keys(answers).length
	}, [practiceSetItems, answers])

	const submitAnswers = async (practiceSet: PracticeSet, completedAnswers: Record<number, string>) => {
		const inputs: Record<number, object> = {}
		for (const item of practiceSetItems) {
			if (completedAnswers[item.id] !== undefined) {
				inputs[item.id] = { answer: completedAnswers[item.id] }
			} else {
				inputs[item.id] = { answer: null }
			}
		}

		return await api(`/v2/practice/sets/${practiceSet.id}/answer`, {
			method: 'POST',
			body: {
				inputs,
			},
		})
	}

	const onSubmitClicked = async () => {
		setIsSubmittingAnswersLoading(true)
		await submitAnswers(practiceSet, answers)
			.then((result: SentencesPracticeAnswer) => {
				setAnswerResult(result)
				onPracticeAnswersSubmitted()
			})
			.catch((error) => {
				toast(error.data?.message || 'Unknown error')
			})
			.finally(() => setIsSubmittingAnswersLoading(false))
	}

	const onExplainMoreClicked = async (practiceSetItemId: number) => {
		setLoadingExplainMoreId(practiceSetItemId)
		await api(`/v2/practice/sets/${practiceSetItemId}/more-explanation-for-correction-note`, {
			method: 'POST',
		})
			.then((result: SentencesPracticeAnswer) => {
				setAnswerResult(result)
			})
			.catch((error) => {
				toast(error.data?.message || 'Unknown error')
			})
			.finally(() => setLoadingExplainMoreId(null))
	}

	const onSubmitFollowUpChat = async (practiceSetItemId: number) => {
		setLoadingSubmitFollowUpChatId(practiceSetItemId)
		await api(`/v2/chat/practice/${practiceSetItemId}/practice-corrections-followup-chat`, {
			method: 'POST',
			body: {
				query: followUpChatContent,
			},
		})
			.then((result: AiQueryResponse) => {
				dispatch('show-chat-data', result)
			})
			.catch((error) => {
				toast(error.data?.message || 'Unknown error')
			})
			.finally(() => setLoadingSubmitFollowUpChatId(null))
	}

	// Container animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.2,
			},
		},
	}

	// Individual element animation variants
	const itemVariants = {
		hidden: { y: -100, opacity: 0 },
		show: {
			y: 0,
			opacity: 1,
			transition: {
				type: 'spring',
				stiffness: 50,
				damping: 10,
				mass: 0.8,
			},
		},
	}

	return (
		<div className="dark:text-white text-black pb-2 h-full">
			<div className="px-8">
				<div className="flex flex-col justify-center gap-2">
					<div className="flex flex-col">
						<div className="flex items-center mt-2">
							<div className="border-t border-2 border-gray-400 flex-grow"></div>
							<div className="px-3">{t('Sentences')}</div>
							<div className="border-t border-2 border-gray-400 flex-grow"></div>
						</div>
					</div>
					{currentPracticeStatus != 'finished' && (
						<>
							<div>
								<p className="text-sm">
									{t('translate_swipe', {
										lang: t(learningLanguageCode),
									})}
								</p>
							</div>
						</>
					)}
				</div>
			</div>
			<AnimatePresence mode="wait">
				{isSubmittingAnswersLoading ? (
					<motion.div
						key="anki-submit-loading"
						className="flex flex-col justify-center items-center gap-2 h-full">
						<span>{t('Submitting')}</span>
						<Loader2 className="h-4 w-4 animate-spin" />
					</motion.div>
				) : currentPracticeStatus === 'finished' && answerResult !== undefined ? (
					<motion.div
						className="flex flex-col w-full min-h-full"
						variants={containerVariants}
						initial="hidden"
						animate="show">
						<div className="flex flex-col items-center justify-center mb-7">
							<PracticeSetResultSummary
								correct_count={answerResult.correct_count}
								items_count={answerResult.items_count}
								practiced_at={answerResult.practiced_at || ''}
							/>
						</div>
						<motion.div className="grid grid-cols-1 md:grid-cols-2 gap-4 mx-2" variants={containerVariants}>
							{Object.entries(answerResult?.result).map(([practiceItemId, item], idx) => (
								<motion.div
									key={practiceItemId}
									className={`${themeColor === 'dark' ? 'bg-appdarkgray' : 'bg-applightgray'} rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 p-2`}
									variants={itemVariants}>
									<div className="flex justify-between my-1">
										<div className="text-base font-base">{idx + 1}</div>
										<div className="flex items-center">
											{item.is_correct ? (
												<div className="flex justify-center items-center rounded bg-appgreen px-2">
													<span className="me-1 text-xs text-black">{t('Correct')}</span>
													<Check className="w-5 h-5 text-black" />
												</div>
											) : (
												<div className="flex justify-center items-center rounded bg-appred px-2">
													<span className="me-1 text-xs text-white">{t('Incorrect')}</span>
													<X className="w-5 h-5 text-white" />
												</div>
											)}
										</div>
									</div>
									<div className="flex flex-col gap-2 pb-2">
										<div>
											<span className="text-xs flex my-1 font-medium select-none">
												{t('Sentence to translate')}
											</span>
											<div className="font-normal mx-2">{item.correct_translation}</div>
										</div>
										<div className={`bg-black/10 p-2 rounded border-primary border`}>
											<span className="text-xs flex my-1 font-medium justify-end">
												{t('Your translation')}
											</span>
											{item.user_translation !== null && item.user_translation.length > 0 ? (
												<div className="font-base">{item.user_translation}</div>
											) : (
												<div className="flex items-center gap-1.5 select-none">
													<CircleAlert className="w-4 h-4" />
													<span className="font-normal italic">
														{t('No translation provided.')}
													</span>
												</div>
											)}
										</div>
										{item.correction_notes !== null && item.correction_notes.length > 0 && (
											<div className="relative p-2 border-none rounded-lg mt-3 mb-2 dark:bg-[#5d5d5d] bg-[#b1b1b1]">
												<div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
													<span
														className={`${themeColor === 'dark' ? 'bg-appdarkgray' : 'bg-applightgray'} px-4 py-0.5 font-normal text-sm rounded flex gap-2 items-center select-none`}>
														<PenLine className="w-4 h-4" />
														<span>{t('Correction notes')}</span>
													</span>
												</div>
												<div className="mt-2.5 mx-1 text-sm dark:text-gray-200 font-normal markdown">
													<Markdown remarkPlugins={[remarkBreaks]}>
														{item.correction_notes}
													</Markdown>
												</div>
												<div className="flex flex-col justify-end gap-3 mx-1.5 my-1.5 mt-4">
													<div className="flex justify-end gap-3">
														<LoadingButton
															loading={loadingExplainMoreId === parseInt(practiceItemId)}
															onClick={() =>
																onExplainMoreClicked(parseInt(practiceItemId))
															}
															variant="secondary"
															className="gap-1.5 p-2 h-7 active:bg-primary/10"
															size="xs">
															<span>{t('Explain more')}</span>
														</LoadingButton>
														<Button
															onClick={() =>
																setIsFollowUpChatContentBoxShown(
																	isFollowUpChatContentBoxShown ===
																		parseInt(practiceItemId)
																		? null
																		: parseInt(practiceItemId),
																)
															}
															variant="secondary"
															className="gap-1.5 p-2 h-7 active:bg-primary/10"
															size="xs">
															<Sparkles className="w-4 h-4" />
															<span className="flex items-center">{t('Ask AI')}</span>
														</Button>
													</div>
													{isFollowUpChatContentBoxShown === parseInt(practiceItemId) && (
														<div className="flex gap-2">
															<Textarea
																dir="auto"
																className="resize-none h-16 p-1.5"
																style={{
																	backgroundColor:
																		themeColor === 'dark' ? '#3d3d3d' : '#e1e1e1',
																}}
																autoCapitalize="none"
																autoComplete="off"
																spellCheck="false"
																placeholder={t('Enter your response')}
																value={followUpChatContent}
																onChange={(e) => setFollowUpChatContent(e.target.value)}
																onKeyDown={async (e: any) => {
																	if (e.key === 'Enter') {
																		if (e.shiftKey) {
																			// Shift+Enter → let the browser insert a newline & scroll
																			return
																		}
																		// Plain Enter → send the message
																		e.preventDefault()
																		await onSubmitFollowUpChat(
																			parseInt(practiceItemId),
																		)
																	}
																}}
															/>
															<LoadingButton
																disabled={followUpChatContent.length === 0}
																loading={
																	loadingSubmitFollowUpChatId ===
																	parseInt(practiceItemId)
																}
																onClick={async () =>
																	await onSubmitFollowUpChat(parseInt(practiceItemId))
																}
																variant="default"
																className="h-16">
																{t('Submit')}
															</LoadingButton>
														</div>
													)}
												</div>
											</div>
										)}
										{item.vocabulary_used.length > 0 && (
											<div className="flex flex-col gap-1">
												<span className="text-xs flex my-1 font-medium">
													{t('Vocabulary used from your library')}
												</span>
												<div className="w-full overflow-x-auto whitespace-nowrap">
													{item.vocabulary_used.map((item, index) => (
														<Button
															key={index}
															className="mb-3 me-2 h-6"
															onClick={() => handleOpenWordDetailsModal(item)}>
															{item.word}
														</Button>
													))}
												</div>
											</div>
										)}
										{item.sentence !== null && item.sentence.length > 0 && (
											<ExpandableButton
												defaultExpanded={!item.is_correct}
												buttonClassName="py-2 px-2.5"
												buttonVariant="outline"
												buttonContent={
													<div className="flex items-center justify-between w-full">
														<div className="flex items-center gap-2">
															<CheckCheck className="w-4 h-4" />
															{t('Correct translation')}
														</div>
														<ChevronDown className="h-4 w-4" />
													</div>
												}
												expandedContent={
													<div
														className="font-normal text-start select-text"
														dir={availableLearningLanguages[learningLanguageCode]?.dir}>
														{item.sentence}
													</div>
												}
											/>
										)}
									</div>
								</motion.div>
							))}
						</motion.div>
						<div className="w-full flex flex-col justify-center items-center gap-3 py-3 mt-auto">
							<Button onClick={onClosePracticeSet} className="h-6 px-5" variant="default">
								{availableAppLanguages[appLanguageCode].dir === 'rtl' ? (
									<ChevronRight className="h-4 w-4" />
								) : (
									<ChevronLeft className="h-4 w-4" />
								)}
								{t('Return to overview')}
							</Button>
						</div>
					</motion.div>
				) : practiceSetItems.length > 0 ? (
					<Carousel
						ref={carouselRef}
						className={`w-full h-full ${isTextareaFocused ? 'overflow-y-auto' : 'overflow-y-hidden'}`}
						onKeyDownCapture={undefined}
						opts={{
							direction: availableAppLanguages[appLanguageCode]?.dir as 'ltr' | 'rtl',
						}}>
						<div className="w-full flex flex-row justify-center">
							<div className="w-full text-start p-5 pb-2 select-none">
								<div className="flex justify-evenly gap-5 my-5">
									<div>
										<Button
											onClick={() => dispatch('end-practice')}
											variant="ghost"
											className="h-8 text-muted-foreground">
											{t('Leave')}
										</Button>
									</div>
									<div className="w-full">
										<Button
											onClick={onSubmitClicked}
											variant={
												practiceSetItems.every((item) => answers[item.id])
													? 'default'
													: 'secondary'
											}
											className="w-full flex justify-center items-center h-8">
											<CheckCheck className="w-4 h-4 mx-2" />
											<span>{t('Submit answers')}</span>
										</Button>
										<div className="text-center mt-1">
											<span className="text-xs text-muted-foreground">
												{remainingAnswersCount <= 0
													? t('Ready to submit!')
													: t('sentences_remain', { count: remainingAnswersCount })}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="w-full flex flex-row justify-between p-4 pt-2">
							<div className="flex justify-center items-center">
								<span className="font-base text-sm select-none">
									{t('translate_to', { lang: t(learningLanguageCode) })}
								</span>
							</div>
							<div className="flex flex-none">
								<CarouselDots className="mt-2" />
							</div>
						</div>
						<CarouselContent className="h-full">
							{practiceSetItems.map((item, index) => (
								<CarouselItem key={index} className="bg-transparent px-2">
									<div className="flex min-h-12">
										<div className="w-12 h-6 items-start">
											<Languages className="mt-1" />
										</div>
										<div className="flex-1">{item.translated_sentence}</div>
									</div>
									<Textarea
										dir="auto"
										className="mt-5 resize-none h-[15%]"
										style={{ backgroundColor: themeColor === 'dark' ? '#3d3d3d' : '#e1e1e1' }}
										autoCapitalize="none"
										autoComplete="off"
										spellCheck="false"
										placeholder={t('Enter your response')}
										value={answers[item.id] || ''}
										onChange={(e) => handleTextAreaChange(item.id, e.target.value)}
										onFocus={() => setIsTextareaFocused(true)}
										onBlur={() => {
											setTimeout(() => {
												setIsTextareaFocused(false)
												carouselRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
											}, 0)
										}}
									/>
								</CarouselItem>
							))}
						</CarouselContent>
					</Carousel>
				) : (
					<></>
				)}
			</AnimatePresence>
		</div>
	)
}

export default SentencesPractice
